import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

interface NavigationState {
  history: string[];
  referrer: string | null;
}

interface NavigationContextType {
  navigationState: NavigationState;
  addToHistory: (path: string) => void;
  clearHistory: () => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export function NavigationProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [navigationState, setNavigationState] = useState<NavigationState>({
    history: [],
    referrer: null,
  });

  useEffect(() => {
    // Add current path to history
    setNavigationState(prev => ({
      ...prev,
      history: [...prev.history, location.pathname].slice(-10), // Keep last 10 entries
    }));
  }, [location.pathname]);

  const addToHistory = (path: string) => {
    setNavigationState(prev => ({
      ...prev,
      history: [...prev.history, path].slice(-10),
    }));
  };

  const clearHistory = () => {
    setNavigationState({
      history: [],
      referrer: null,
    });
  };

  return (
    <NavigationContext.Provider value={{ navigationState, addToHistory, clearHistory }}>
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigation() {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
}
